import {
  BuildDiningSelectionForCartInput,
  DiningSelectionMetadata,
  SelectedRestaurantReservation,
} from '@msc-b2c/interfaces/interfaces';

/**
 * Dining selection payload mappers.
 *
 * Goal:
 * - Convert UI reservation state into cart/already-purchased metadata contract.
 * - Preserve thematicBookingId when a reservation is edited (same restaurant/date).
 */

/**
 * Sorts reservations by date/time to keep payload and recap rendering deterministic.
 */
export const sortReservationsChronologically = (
  reservations: ReadonlyArray<SelectedRestaurantReservation>
): SelectedRestaurantReservation[] => {
  return [...reservations].sort((left, right) => {
    const leftTimestamp = new Date(`${left.date}T${left.time}`).getTime();
    const rightTimestamp = new Date(`${right.date}T${right.time}`).getTime();

    if (!Number.isNaN(leftTimestamp) && !Number.isNaN(rightTimestamp)) {
      return leftTimestamp - rightTimestamp;
    }

    const leftDateCompare = left.date.localeCompare(right.date);
    if (leftDateCompare !== 0) {
      return leftDateCompare;
    }

    return left.time.localeCompare(right.time);
  });
};

/**
 * Creates a lookup map of existing reservations keyed by restaurant code,
 * so revised payloads can reuse thematicBookingId when appropriate.
 */
const buildExistingReservationsMap = (
  preselectedReservations: BuildDiningSelectionForCartInput['preselectedReservations'] = []
): Map<string, Array<{ date: string; thematicBookingId: string }>> => {
  const map = new Map<string, Array<{ date: string; thematicBookingId: string }>>();

  preselectedReservations.forEach((reservation) => {
    const key = reservation.restaurantCode;
    if (!key || !reservation.day || !reservation.thematicBookingId) {
      return;
    }

    const existing = map.get(key) ?? [];
    existing.push({ date: reservation.day, thematicBookingId: reservation.thematicBookingId });
    map.set(key, existing);
  });

  return map;
};

/**
 * Builds DiningSelectionMetadata used by parent flows on apply/confirm.
 *
 * Responsibility split:
 * - mapping + filtering valid reservations
 * - restoring thematicBookingId for revised reservations
 * - attaching flow-level metadata (entry point, mode, limits)
 */
export const buildDiningSelectionForCart = ({
  reservationsSource,
  preselectedReservations,
  entryPoint,
  reservationMode,
  isReserveNowDisabled,
  preselectedReservationId,
  maximumSlotsBookable,
}: BuildDiningSelectionForCartInput): DiningSelectionMetadata => {
  const existingReservationsMap = buildExistingReservationsMap(preselectedReservations);

  const reservations = sortReservationsChronologically(reservationsSource)
    .filter((reservation) =>
      Boolean(reservation.date && reservation.time && reservation.restaurantCode)
    )
    .map((reservation) => {
      let thematicBookingId: string | undefined;

      const existingForRestaurant = existingReservationsMap.get(reservation.restaurantCode);
      if (existingForRestaurant && reservation.date) {
        const exactMatch = existingForRestaurant.find((match) => match.date === reservation.date);
        if (exactMatch) {
          thematicBookingId = exactMatch.thematicBookingId;
        }
      }

      return {
        day: reservation.date,
        hour: reservation.time,
        restaurantCode: reservation.restaurantCode,
        ...(reservation.restaurantName && { restaurantName: reservation.restaurantName }),
        ...(thematicBookingId && { thematicBookingId }),
      };
    });

  return {
    entryPoint,
    ...(reservationMode && { reservationMode }),
    ...(isReserveNowDisabled && { timeslotsSelectionNotAvailable: true }),
    ...(reservations.length > 0 && { reservations }),
    ...(preselectedReservationId && {
      reservationId: preselectedReservationId,
    }),
    maximumSlotsBookable,
  };
};
